import { useMemo, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import _ from 'lodash';
import { Table } from 'antd';
import { fileNameEdit } from 'helpers/FileUtils';
import Text from 'antd/es/typography/Text';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/consumi_net_sales/');

const format = (v) => `${v}%`;

const defaultState = {
  table1: [],
  table2: [],
  total1: {},
  total2: {},
  barChart: [],
  total: {},
  tableData: [],
  barChangeLine: [],
  keys: [],
  bar2: {
    barChart: [],
    barChangeLine: [],
    tableData: [],
    maxValue: 0,
  },
};

const useConsumiNetSales = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [data, setData] = useState(defaultState);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const [columns, setColumns] = useState([
    {
      title: t('CATEGORIA MERCEOLOGICA'),
      dataIndex: 'Net Sales',
      fixed: true,
    },
    {
      title: t('Net Sales YTD(€)'),
      dataIndex: 'Net Sales YTD(€)',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Net Sales LY(€)'),
      dataIndex: 'Net Sales LY(€)',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Net Sales VS LY +/-(%)'),
      dataIndex: 'Net Sales VS LY +/-(%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Inc. % Net Sales YTD'),
      dataIndex: 'Inc. % Net Sales YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Inc. % Net Sales LY'),
      dataIndex: 'Inc. % Net Sales LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('ATV YTD (€)'),
      dataIndex: 'ATV YTD (€)',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('ATV LY (€)'),
      dataIndex: 'ATV LY (€)',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('ATV VS LY +/-(%)'),
      dataIndex: 'ATV VS LY +/-(%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let table1, table2;

        if (!isAllReports) {
          const {
            data: { table1: backTable1, table2: backTable2 },
          } = await api.getFootfalls({ params });
          table1 = backTable1;
          table2 = backTable2;
        } else {
          const { table1: backTable1, table2: backTable2 } = allReportsData;
          table1 = backTable1;
          table2 = backTable2;
        }

        const maxValueFroLy1 = _.maxBy(table1.data, 'Inc. % Net Sales YTD')[
          'Inc. % Net Sales YTD'
        ];
        const maxValueFroYtd1 = _.maxBy(table1.data, 'Inc. % Net Sales LY')[
          'Inc. % Net Sales LY'
        ];

        const maxValue1 =
          maxValueFroLy1 > maxValueFroYtd1 ? maxValueFroLy1 : maxValueFroYtd1;

        const maxValueFroLy2 = _.maxBy(table2.data, 'Inc. % Net Sales YTD')[
          'Inc. % Net Sales YTD'
        ];
        const maxValueFroYtd2 = _.maxBy(table2.data, 'Inc. % Net Sales LY')[
          'Inc. % Net Sales LY'
        ];

        const maxValue2 =
          maxValueFroLy2 > maxValueFroYtd2 ? maxValueFroLy2 : maxValueFroYtd2;

        const barChart = table1.data.map((item) => ({
          date: item['Net Sales'],
          'Inc. % Net Sales YTD': item['Inc. % Net Sales YTD'],
          'Inc. % Net Sales YTDColor': '#61cdbb',
          'Inc. % Net Sales LY': item['Inc. % Net Sales LY'],
          'Inc. % Net Sales LYColor': '#f47560',
        }));

        const barChangeLine = {
          id: 'Net Sales VS LY +/-(%)',
          data: table1.data.map((item) => ({
            x: item['Net Sales'],
            y: item['Net Sales VS LY +/-(%)'],
            tooltipKeys: [
              {
                key: 'Inc. % Net Sales YTD',
                value: Helpers.changeDotToComa(
                  format(item['Inc. % Net Sales YTD']),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Inc. % Net Sales LY',
                value: Helpers.changeDotToComa(
                  format(item['Inc. % Net Sales LY']),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Net Sales VS LY +/-(%)',
                value: Helpers.changeDotToComa(
                  format(item['Net Sales VS LY +/-(%)']),
                ),
                color: '#1f77b4',
                percent: '',
              },
              {
                key: 'ATV VS LY +/-(%)',
                value: Helpers.changeDotToComa(
                  format(item['ATV VS LY +/-(%)']),
                ),
                color: '#ff7f0e',
                percent: '',
              },
            ],
          })),
        };

        const barChangeLine2 = {
          id: 'ATV VS LY +/-(%)',
          data: table1.data.map((item) => ({
            x: item['Net Sales'],
            y: item['ATV VS LY +/-(%)'],
            tooltipKeys: [],
          })),
        };

        const bar2Chart2 = table2.data.map((item) => ({
          date: item['Net Sales'],
          'Inc. % Net Sales YTD': item['Inc. % Net Sales YTD'],
          'Inc. % Net Sales YTDColor': '#61cdbb',
          'Inc. % Net Sales LY': item['Inc. % Net Sales LY'],
          'Inc. % Net Sales LYColor': '#f47560',
        }));

        const bar2ChangeLine = {
          id: 'Net Sales VS LY +/-(%)',
          data: table2.data.map((item) => ({
            x: item['Net Sales'],
            y: item['Net Sales VS LY +/-(%)'],
            tooltipKeys: [
              {
                key: 'Inc. % Net Sales YTD',
                value: Helpers.changeDotToComa(
                  format(item['Inc. % Net Sales YTD']),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Inc. % Net Sales LY',
                value: Helpers.changeDotToComa(
                  format(item['Inc. % Net Sales LY']),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Net Sales VS LY +/-(%)',
                value: Helpers.changeDotToComa(
                  format(item['Net Sales VS LY +/-(%)']),
                ),
                color: '#1f77b4',
                percent: '',
              },
              {
                key: 'ATV VS LY +/-(%)',
                value: Helpers.changeDotToComa(
                  format(item['ATV VS LY +/-(%)']),
                ),
                color: '#ff7f0e',
                percent: '',
              },
            ],
          })),
        };

        const bar2ChangeLine2 = {
          id: 'ATV VS LY +/-(%)',
          data: table2.data.map((item) => ({
            x: item['Net Sales'],
            y: item['ATV VS LY +/-(%)'],
            tooltipKeys: [],
          })),
        };

        setData({
          table1: table1.data,
          table2: table2.data,
          total1: table1.total,
          total2: table2.total,
          maxValue: maxValue1,
          barChart,
          tableData: [],
          barChangeLine: [barChangeLine, barChangeLine2],
          bar2: {
            barChart: bar2Chart2,
            barChangeLine: [bar2ChangeLine, bar2ChangeLine2],
            tableData: [],
            maxValue: maxValue2,
          },
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setErrors(UIHelper.showAnalyticsError(e));
        setData(defaultState);
      } finally {
        setLoading(false);
        if (isPrint) {
          const event = new CustomEvent('status', { detail: 'ok' });

          setTimeout(() => {
            window.dispatchEvent(event);
          }, 5000);
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  const columns2 = useMemo(() => {
    return [
      {
        title: t('Segmento'),
        dataIndex: 'Net Sales',
        fixed: true,
      },
      {
        title: t('Net Sales YTD(€)'),
        dataIndex: 'Net Sales YTD(€)',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('Net Sales LY(€)'),
        dataIndex: 'Net Sales LY(€)',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('Net Sales VS LY +/-(%)'),
        dataIndex: 'Net Sales VS LY +/-(%)',
        render: (v) => Helpers.changeDotToComa(format(v)),
      },
      {
        title: t('Inc. % Net Sales YTD'),
        dataIndex: 'Inc. % Net Sales YTD',
        render: (v) => Helpers.changeDotToComa(format(v)),
      },
      {
        title: t('Inc. % Net Sales LY'),
        dataIndex: 'Inc. % Net Sales LY',
        render: (v) => Helpers.changeDotToComa(format(v)),
      },
      {
        title: t('ATV YTD (€)'),
        dataIndex: 'ATV YTD (€)',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('ATV LY (€)'),
        dataIndex: 'ATV LY (€)',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('ATV VS LY +/-(%)'),
        dataIndex: 'ATV VS LY +/-(%)',
        render: (v) => Helpers.changeDotToComa(format(v)),
      },
    ];
  }, []);

  const summary = useCallback(() => {
    const { total1 = {} } = data;

    delete total1['Net Sales'];

    const tableKeys = Object.keys(total1);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total1[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data, columns]);

  const summary2 = useCallback(() => {
    const { total2 = {} } = data;

    delete total2['Net Sales'];

    const tableKeys = Object.keys(total2);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total2[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  return {
    columns2,
    columns,
    data,
    filters,
    format,
    title,
    loading,
    handleDownloadExcel,
    searchParams,
    summary,
    summary2,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useConsumiNetSales;
